import React, { useState, useEffect, useCallback } from 'react';
import api from '../../../services/api';
import { Button } from '../../ui/button';
import { toast } from 'sonner';
import { Loader2, Save, Percent } from 'lucide-react';

const fmt = (n) => Number(n || 0).toLocaleString('pt-BR');

export default function OilAdminJackpotRate({ seasonId, onSaved }) {
  const [data, setData] = useState(null);
  const [rate, setRate] = useState('');
  const [mag, setMag] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/admin/oil/season/${seasonId}/jackpot-rate`);
      setData(r.data);
      setRate(String(r.data.rate_pct));
      setMag(String(r.data.magnates_pct));
    } catch { toast.error('Erro ao carregar a taxa do Jackpot.'); }
  }, [seasonId]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (reason.trim().length < 3) { toast.error('Informe o motivo da alteração.'); return; }
    setBusy(true);
    try {
      const r = await api.patch(`/admin/oil/season/${seasonId}/jackpot-rate`, {
        rate_pct: Number(rate), magnates_pct: Number(mag), reason });
      toast.success(`Jackpot agora recebe ${r.data.rate_pct}% de cada recarga.`);
      setReason('');
      await load();
      onSaved?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao salvar.'); }
    setBusy(false);
  };

  if (!data) {
    return <div className="flex justify-center py-5"><Loader2 className="h-5 w-5 animate-spin text-orange-500" /></div>;
  }

  const prevRate = Number(rate) || 0;
  const prevMag = Math.min(100, Math.max(0, Number(mag) || 0));
  const prevTotal = Math.floor(100000 * prevRate) / 100;
  const prevMagCoins = Math.floor((prevTotal * prevMag) / 100);

  return (
    <div className="rounded-xl border border-amber-300 bg-amber-50/60 p-3" data-testid="oil-admin-jackpot-rate">
      <p className="mb-2 flex items-center gap-1.5 text-xs font-black uppercase text-amber-800">
        <Percent className="h-3.5 w-3.5" /> Taxa do Jackpot
      </p>
      <p className="mb-3 text-[11px] leading-relaxed text-amber-900">
        Quanto de cada recarga paga vira prêmio dos rankings. Vale só para as recargas
        seguintes — o que já está acumulado continua igual. Hoje o cliente recebe{' '}
        <b>{data.black_coin_pct}%</b> em Black Coins (isso não muda aqui).
      </p>
      <div className="grid gap-2 sm:grid-cols-3">
        <label className="text-[10px] font-bold text-slate-500">
          <span className="block">% da recarga para o Jackpot</span>
          <input type="number" step="0.05" min="0" max="5" value={rate}
            data-testid="oil-jackpot-rate-input" onChange={(e) => setRate(e.target.value)}
            className="mt-0.5 h-9 w-full rounded-lg border px-2 text-sm font-bold" />
        </label>
        <label className="text-[10px] font-bold text-slate-500">
          <span className="block">% para Magnatas (o resto vai aos Corretores)</span>
          <input type="number" step="1" min="0" max="100" value={mag}
            data-testid="oil-jackpot-magnates-pct-input" onChange={(e) => setMag(e.target.value)}
            className="mt-0.5 h-9 w-full rounded-lg border px-2 text-sm font-bold" />
        </label>
        <label className="text-[10px] font-bold text-slate-500">
          <span className="block">Motivo da alteração (obrigatório)</span>
          <input value={reason} onChange={(e) => setReason(e.target.value)}
            data-testid="oil-jackpot-rate-reason" placeholder="Ex.: ajuste de premiação"
            className="mt-0.5 h-9 w-full rounded-lg border px-2 text-xs" />
        </label>
      </div>
      <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
        <p className="text-[11px] font-bold text-slate-600" data-testid="oil-jackpot-rate-preview">
          Recarga de 100.000 moedas → Jackpot <b className="text-amber-700">{fmt(prevTotal)}</b>{' '}
          ({fmt(prevMagCoins)} Magnatas + {fmt(prevTotal - prevMagCoins)} Corretores)
          <span className="ml-2 text-slate-400">· valendo agora: {data.rate_pct}% ·
            {' '}{data.magnates_pct}/{data.shareholders_pct}</span>
        </p>
        <Button size="sm" disabled={busy} onClick={save} data-testid="oil-jackpot-rate-save"
          className="bg-amber-600 text-white hover:bg-amber-700">
          <Save className="mr-1 h-3.5 w-3.5" /> Salvar taxa
        </Button>
      </div>
    </div>
  );
}
